import { useTopics } from '@/hooks/useTopics';
import { useFeaturedArticles, useArticlesGroupedByTopic } from '@/hooks/useArticlesByTopic';
import LearnCarouselSection from './LearnCarouselSection';
import TopicCardsCarousel from './TopicCardsCarousel';
import { ArrowLeft } from '@phosphor-icons/react';

interface LearnContentSectionProps {
  activeTopic: string;
  onTopicChange: (topic: string) => void;
  onLastSectionBgChange?: (bg: 'white' | 'platinum') => void;
}

export default function LearnContentSection({ 
  activeTopic, 
  onTopicChange,
  onLastSectionBgChange 
}: LearnContentSectionProps) {
  const { data: topics, isLoading: topicsLoading } = useTopics();
  const { data: featuredArticles, isLoading: featuredLoading } = useFeaturedArticles();
  const { data: articlesGrouped, isLoading: articlesLoading } = useArticlesGroupedByTopic();

  const activeTopics = topics?.filter(t => t.is_active) || [];
  
  // Filter to only topics that have articles, maintaining CMS sort order
  const topicsWithArticles = activeTopics.filter(topic => {
    const articles = articlesGrouped?.[topic.id];
    return articles && articles.length > 0;
  });

  // When a specific topic is selected, filter to just that topic
  const visibleTopics = activeTopic === 'All' 
    ? topicsWithArticles 
    : topicsWithArticles.filter(t => t.name === activeTopic);

  // Calculate the last section's background color for CTA gradient blending
  // When filtered: always white
  // When "All": depends on the count of visible topic sections (alternating pattern)
  const getLastSectionBg = (): 'white' | 'platinum' => {
    if (activeTopic !== 'All') {
      return 'white'; // Filtered sections always have white background
    }
    
    if (visibleTopics.length === 0) {
      return 'platinum'; // Topics carousel is platinum when no topic sections
    }
    
    // Last topic section index determines its background
    // Index 0 = white, Index 1 = platinum, Index 2 = white, etc. (alternating from Topics carousel platinum)
    const lastIndex = visibleTopics.length - 1;
    return lastIndex % 2 === 0 ? 'white' : 'platinum';
  };

  // Notify parent of background change when data loads
  const lastBg = getLastSectionBg();
  if (onLastSectionBgChange && !topicsLoading && !articlesLoading) {
    // Use setTimeout to avoid state update during render
    setTimeout(() => onLastSectionBgChange(lastBg), 0);
  }

  return (
    <main>
      {/* Featured Articles Section - only show when "All" is selected */}
      {activeTopic === 'All' && (
        <LearnCarouselSection
          id="featured"
          title="Start Here"
          articles={(featuredArticles || []).map(a => ({
            ...a,
            topic_name: a.topic_name || undefined,
            topic_color: a.topic_color || undefined,
          }))}
          variant="featured"
          isLoading={featuredLoading}
          className="bg-background"
          boldTitle
          position={1}
        />
      )}

      {/* Topic Cards Carousel - only show when "All" is selected, has platinum bg */}
      {activeTopic === 'All' && (
        <TopicCardsCarousel
          activeTopic={activeTopic}
          onTopicChange={onTopicChange}
        />
      )}

      {/* Mobile: Back to All button when filtered */}
      {activeTopic !== 'All' && (
        <div className="md:hidden bg-background px-4 pt-4 -mb-4">
          <button
            onClick={() => onTopicChange('All')}
            className="inline-flex items-center gap-1.5 text-coal hover:text-carbon transition-colors text-sm font-medium tracking-wide"
          >
            <ArrowLeft weight="bold" size={14} />
            Back to All
          </button>
        </div>
      )}

      {/* Topic-based Sections - alternating backgrounds based on VISIBLE sections */}
      {/* Topics carousel is platinum, so first topic section is white to alternate */}
      {!topicsLoading && !articlesLoading && visibleTopics.map((topic, visibleIndex) => {
        const articles = articlesGrouped?.[topic.id] || [];
        
        // When filtered to single topic, always use white background
        // When "All": alternate starting with white (since Topics carousel is platinum)
        const bgClass = activeTopic === 'All' 
          ? (visibleIndex % 2 === 0 ? "bg-white" : "bg-platinum")
          : "bg-background";
        
        return (
          <LearnCarouselSection
            key={topic.id}
            id={topic.slug}
            title={topic.name}
            subtitle={topic.description}
            articles={articles.map(a => ({
              ...a,
              topic_name: a.topic_name || undefined,
              topic_color: a.topic_color || undefined,
            }))}
            variant="feed"
            onSeeMore={activeTopic === 'All' ? () => onTopicChange(topic.name) : undefined}
            className={bgClass}
            boldTitle
            position={visibleIndex + 3}
          />
        );
      })}

      {/* Loading state for topics */}
      {(topicsLoading || articlesLoading) && (
        <LearnCarouselSection
          title="Loading..."
          articles={[]}
          isLoading={true}
          className="bg-platinum"
        />
      )}
    </main>
  );
}
